'use client';

import { ActionKitBuilder, actionKitBuilder } from '../action-kit';
import type {
  ActionKitBuilderErrorMapper,
  ActionKitErrorBase,
  ActionKitErrorUnknown,
  ActionKitResult,
  InferActionKitTypes,
} from '../action-kit/types';
import type {
  FetchContextState,
  FetchProviderProps,
  MaxTwoArgs,
} from './types';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactElement,
} from 'react';

export function createFetchContext<
  T extends (...args: MaxTwoArgs<any>) => Promise<ActionKitResult<any, TError>>,
  TError extends ActionKitErrorBase,
  TActionError extends ActionKitErrorBase | undefined = undefined
>(
  action: T,
  options?: {
    onActionError?: TActionError extends undefined
      ? undefined
      : ActionKitBuilderErrorMapper<Exclude<TActionError, undefined>>;
  },
) {
  type Infer = InferActionKitTypes<T>;
  type Result = Infer['result'];
  type Args = Infer['args'];
  type Query = Args extends [infer Q] | [infer Q, any] ? Q : undefined;

  const FetchContext = createContext<
    FetchContextState<
      Result,
      TError | ActionKitErrorUnknown | Exclude<TActionError, undefined>,
      Query
    >
  >({
    loading: true,
    result: undefined,
    query: {} as Query,
    clearData: () => {
      throw new Error('Clear No implementado');
    },
    updateQuery: () => {
      throw new Error('Update Query No implementado');
    },
    refresh: () => {
      throw new Error('refresh No implementado');
    },
  });

  const useFetch = () => {
    return useContext(FetchContext);
  };

  const useFetchData = () => {
    const data = useContext(FetchContext);

    if (data.loading || !data.result) {
      throw new Error('Aún está cargando o no hay datos');
    }
    if (!data.result.success) {
      throw new Error('Error al cargar los datos: ' + data.result.data.code);
    }
    return data.result.data;
  };

  const FetchProvider = ({
    fetchImmediately: fetchImmediatelyProp = false,
    children,
    defaultQuery,
    params,
  }: FetchProviderProps<Args>): ReactElement => {
    const [isFetching, setIsFetching] = useState(fetchImmediatelyProp);
    const [result, setResult] = useState<
      ActionKitResult<
        Result,
        TError | ActionKitErrorUnknown | Exclude<TActionError, undefined>
      >
    >();
    const [query, setQuery] = useState<Query>(defaultQuery as Query);
    const [initialized, setInitialized] = useState(false);
    const initializedRef = useRef(false);
    const fetchImmediately = useRef(fetchImmediatelyProp);

    const fetchData = useCallback(
      async (q?: Query) => {
        setIsFetching(true);

        let actionBuilder: ActionKitBuilder<
          Infer['args'],
          ActionKitResult<any, TError>,
          ActionKitErrorUnknown | Exclude<TActionError, undefined>
        > = actionKitBuilder<T, ActionKitErrorUnknown>(action);
        let res;
        if (options?.onActionError) {
          actionBuilder = actionBuilder.onError(
            options.onActionError as ActionKitBuilderErrorMapper<
              Exclude<TActionError, undefined>
            >,
          );
          const actionFunction = actionBuilder.toFunction();
          const response = await actionFunction(q, params);
          if (response.success) {
            res = response.data ?? undefined;
          } else {
            res = response ?? undefined;
          }
        } else {
          const actionFunction = actionBuilder.toFunction();
          const response = await actionFunction(q, params);
          if (response.success) {
            res = response.data ?? undefined;
          } else {
            res = response ?? undefined;
          }
        }
        setResult(res ?? undefined);
        setIsFetching(false);
      },
      [params],
    );

    useEffect(() => {
      setInitialized(true);
    }, []);

    useEffect(() => {
      if (!initialized) {
        return;
      }

      if (initializedRef.current) {
        fetchData(query);
      } else if (fetchImmediately.current) {
        fetchData(query);
      }

      initializedRef.current = true;
    }, [initialized, fetchData, query]);

    const updateQuery = useCallback((q: Query) => {
      setQuery(q);
    }, []);

    const clearData = useCallback(() => {
      setResult(undefined);
    }, []);

    const refresh = useCallback(() => {
      return fetchData(query);
    }, [query, fetchData]);

    const value = useMemo<
      FetchContextState<
        Result,
        TError | ActionKitErrorUnknown | Exclude<TActionError, undefined>,
        Query
      >
    >(
      () => ({
        loading: isFetching,
        result,
        query,
        updateQuery,
        refresh,
        clearData,
      }),
      [isFetching, result, query, updateQuery, refresh, clearData],
    );

    return (
      <FetchContext.Provider value={value}>{children}</FetchContext.Provider>
    );
  };

  return {
    FetchContext,
    useFetch,
    useFetchData,
    FetchProvider,
    FetchConsumer: FetchContext.Consumer,
  };
}
